import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone } from "lucide-react";

import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import {
  FEES,
  FIRM,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_ADDRESS,
  brisbaneBusinessSchema,
  faqSchema,
} from "@/lib/firm";

const DESCRIPTION = `Brisbane conveyancing, wills, probate and business law from Coastside Law. Fixed fees — sales ${FEES.sale}, purchases ${FEES.purchase} incl. GST, grants of probate $3,300 incl GST. Solicitor-led and settled electronically anywhere in Queensland. Michael Klein direct on ${MICHAEL_PHONE}.`;

const FAQS = [
  {
    q: "Do I need a solicitor in Brisbane to buy or sell in Brisbane?",
    a: "No. Queensland conveyancing settles electronically through PEXA, contracts are signed digitally and identity is verified by video. Where your solicitor sits has not affected settlement since paper settlements ended — what matters is that a solicitor, not a clerk, reads your contract.",
  },
  {
    q: "Who will actually do my file?",
    a: `Michael Klein, Legal Practice Director, admitted in 2003 and previously managing partner of GKS Law. His direct line is ${MICHAEL_PHONE} — you deal with him, not a call queue.`,
  },
  {
    q: "What does a Brisbane conveyance cost?",
    a: `Sales are ${FEES.sale} and purchases ${FEES.purchase}, both inclusive of GST, with searches and registry fees at cost. Contract review before you sign is free, and if a residential contract terminates under a standard condition we do not charge a professional fee for the work already done.`,
  },
  {
    q: "Can you act for me on a Brisbane estate?",
    a: "Yes. Grants of probate and letters of administration are filed in the Supreme Court of Queensland at Brisbane regardless of where the executor lives, at a fixed professional fee of $3,300 incl GST, plus the filing fee and published notice at cost.",
  },
  {
    q: "Do I have to come to an office?",
    a: `Only if you want to. Most Brisbane matters run entirely by email, phone and video. If you would rather sit down with someone, the nearest office is ${PENINSULA_ADDRESS}, about 35 minutes north of the CBD, and Michael travels into Brisbane by arrangement.`,
  },
];

const SERVICES = [
  {
    to: "/conveyancing-brisbane" as const,
    title: "Brisbane conveyancing",
    body: `Fixed-fee sales (${FEES.sale}) and purchases (${FEES.purchase}) incl. GST, with free pre-signing contract review.`,
  },
  {
    to: "/probate-solicitor-brisbane" as const,
    title: "Probate & estates",
    body: "Grants of probate and letters of administration in the Supreme Court at Brisbane — $3,300 incl GST fixed fee.",
  },
  {
    to: "/off-the-plan-contracts" as const,
    title: "Off the plan apartments",
    body: "Sunset clauses, disclosure changes, finance timing and the valuation gap on inner-Brisbane towers.",
  },
  {
    to: "/wills-and-estates" as const,
    title: "Wills & EPOA",
    body: "Fixed-fee wills (from $660), will + EPOA packs ($825) and couples packs ($1,499), signed by video or in person.",
  },
  {
    to: "/business-sales" as const,
    title: "Business & commercial",
    body: "Business sales and purchases, retail and commercial leases, management rights and shareholder agreements.",
  },
  {
    to: "/estate-disputes" as const,
    title: "Estate disputes",
    body: "Family provision claims and executor disputes in the Brisbane registry, run on a scoped fee.",
  },
];

const NOTES = [
  {
    t: "Apartments and body corporate stock",
    d: "A large share of Brisbane buying is community titles — inner-city towers, Newstead and West End conversions, and older six-packs in Coorparoo and Annerley. The contract is only half the story: the body corporate records, the sinking fund position, any special levy on the horizon and the by-laws on pets, parking and short stays decide whether the building is a good buy. We read the disclosure statement and the records, not just the front page.",
  },
  {
    t: "Off the plan and the valuation gap",
    d: "Off the plan contracts in Brisbane still carry long sunset dates, developer variation rights and disclosure that changes between signing and registration. Where a valuation at completion comes in under the contract price, the buyer funds the difference in cash. We flag that exposure before you sign, not at settlement.",
  },
  {
    t: "Flood, overlays and older housing",
    d: "Brisbane City Council flood awareness and overlay mapping matters on anything near the river, Oxley, Kedron Brook or Norman Creek, and it affects insurance as much as resale. Pre-1947 character housing carries demolition control that quietly limits what you can do with the site. Both are search-and-advice issues we deal with before the cooling-off period ends.",
  },
  {
    t: "Seller disclosure since August 2025",
    d: "Queensland's seller disclosure regime applies to Brisbane sales like everywhere else — the disclosure statement and prescribed certificates must be given before the buyer signs, or the buyer may be able to terminate at any time up to settlement. Getting this right at listing is the cheapest legal work a seller ever pays for.",
  },
];

const SUBURBS = [
  "Brisbane CBD",
  "New Farm",
  "Newstead",
  "Hamilton",
  "Ascot",
  "Nundah",
  "Chermside",
  "Aspley",
  "Sandgate",
  "Wynnum",
  "Carindale",
  "Indooroopilly",
  "Mount Gravatt",
  "West End",
  "Coorparoo",
];

export const Route = createFileRoute("/brisbane")({
  head: () => ({
    meta: [
      { title: fitTitle(`Brisbane Lawyers — Conveyancing, Wills & Probate | ${FIRM.name}`) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(`Brisbane lawyers — ${FIRM.name}`) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/brisbane" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/brisbane" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(brisbaneBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: BrisbaneHub,
});

function BrisbaneHub() {
  return (
    <>
      <PageHero
        eyebrow="Brisbane"
        title="Brisbane property, estates and business law"
        lead="Fixed fees agreed before the work starts, and a solicitor on your file from the first phone call. Queensland settlements are electronic, so a Brisbane contract is handled the same way whether you are in Bulimba or Bribie — the difference is who reads it."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={MICHAEL_PHONE_HREF}
            className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Call Michael on {MICHAEL_PHONE}
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground"
          >
            Send an enquiry
          </Link>
        </div>
      </PageHero>

      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-serif text-xl text-foreground">
              <Phone className="size-5 text-primary" aria-hidden="true" />
              Direct line
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Michael Klein, Legal Practice Director —{" "}
              <a href={MICHAEL_PHONE_HREF} className="font-semibold text-primary underline">
                {MICHAEL_PHONE}
              </a>
              <br />
              {FIRM.email}
            </p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-serif text-xl text-foreground">
              <MapPin className="size-5 text-primary" aria-hidden="true" />
              Where we meet
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Brisbane matters run by email, phone and video, with electronic settlement through
              PEXA. In person, the nearest office is {PENINSULA_ADDRESS}, by appointment, and
              Michael travels into Brisbane by arrangement.
            </p>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">How we help Brisbane clients</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <span className="font-serif text-xl text-foreground group-hover:text-primary">
                  {s.title}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</span>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read more <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">
            Brisbane property, honestly assessed
          </h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {NOTES.map((n) => (
              <div key={n.t} className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-serif text-xl text-foreground">{n.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Contract review before you sign is free. If a residential contract ends under a standard
            condition — finance, building and pest, or a failed subject to sale — we do not charge a
            professional fee for the work already done, and you pay only the third-party
            disbursements actually incurred.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Suburbs we act in</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Anywhere in the Brisbane City Council area, plus Ipswich, Logan, Redlands and the
            Moreton Bay region to the north.
          </p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {SUBURBS.map((s) => (
              <li
                key={s}
                className="inline-flex rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
              >
                {s}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            North of the river and closer to the bay?{" "}
            <Link to="/moreton-bay" className="font-semibold text-primary underline">
              See our Moreton Bay pages
            </Link>{" "}
            or the{" "}
            <Link to="/scarborough" className="font-semibold text-primary underline">
              Scarborough office
            </Link>
            .
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
